// src/screens/CreatorTeamMembershipsScreen.js
// ============================================================================
// TEAM MEMBERSHIPS (evergreen 3.5b, design 13.4): the captain's own settings.
//
// Split from CreatorMembershipsScreen at the 3.5b merge of live 3.5a
// (STEP3_5B_MERGE_DELTA.md section 5 row 23): that file stays the channel
// screen (tiers, R5), and this one holds the team screen, kind fixed to
// 'team' with no channel path. A team has ONE price (a rung of $4.99, $9.99
// or $14.99 a month; founder call 1, 10-20) and no tiers, so it keeps the
// rung picker and the boolean perks.
//
// Registered in App.js as CreatorTeamMemberships (/creator/team-memberships)
// and exempt from the Terms prompt (HIDE_TOS_PROMPT_ROUTES), so pausing,
// ending, removing a member and turning members-only squad chat off never
// wait on accepting new Terms; the Terms row below is a nudge (the server
// decides).
//
// Data: getMyTeamMembershipSettings and the team setters (functions/modules/
// memberships.js), keyed by teamId. The team comes from route.params.teamId
// (YourTeam, the Hub, the team page, notification taps), else
// getMyStreamInfo().teamMembershipEntry. Only the captain gets the settings;
// a roster member, someone not on the team and a gone team each get one
// read-only line with links. Every change goes through one callable and the
// screen redraws from its reply; after ANY failed change the settings are
// fetched again.
//
// When `available` is false (team memberships dark and no pilot) the screen
// is a management-only view: members, Remove member / Allow again, End all,
// perk removals and members-only squad chat OFF stay; everything that opens
// the team to new members is hidden.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, SafeAreaView, Modal, Pressable, Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../../firebaseConfig';
import { THEME } from '../constants/uiTheme';
import { useError } from '../components/ErrorContext';
import Header from '../components/Header';
import useMembershipsLive from '../hooks/useMembershipsLive';
import {
  getMyTeamMembershipSettings,
  updateMyTeamMembershipSettings,
  endAllMyTeamMemberships,
  setTeamChatMembersOnly,
  setTeamMemberBlocked,
  membershipErrorMessage,
  logMembershipError,
  formatUsd,
  formatMonthlyPrice,
  LISTED_PERKS_BY_KIND,
  PERK_LABELS_BY_KIND,
} from '../utils/membershipPayment';

const C = THEME.colors;
// The member perks a team lists (a team never has a videos perk), then the
// join line (an option for new joins only).
export const TEAM_SNAPSHOT_PERKS = LISTED_PERKS_BY_KIND.team;
export const TEAM_PERK_KEYS = [...LISTED_PERKS_BY_KIND.team, 'joinLine'];
const perkLabel = (k) => PERK_LABELS_BY_KIND.team[k];

// Every captain string (3.5b design 13.4; the team column of the 3.5b
// screen's TEXT_BY_KIND, word for word).
export const TEAM_TEXT = Object.freeze({
  title: 'Team memberships',
  errorTitle: 'Team memberships',
  signIn: 'Sign in to manage your team memberships.',
  pausedHint: "New viewers can't join while you're not accepting new members. Current members keep their perks and renewals.",
  perkRules: "Removing a perk takes effect at each member's first renewal at least 7 days after you change it, "
    + 'and we tell each member. Adding a perk is immediate. Turning off the join shout-out only affects new members; '
    + 'it shows only while the squad is streaming.',
  perkOffNames: Object.freeze({
    badge: 'the team badge in squad chat',
    membersChat: 'members-only squad chat',
  }),
  chatLabel: 'Members-only squad chat',
  chatPausedLabel: 'Members-only squad chat (paused)',
  chatPausedHint: 'Members-only squad chat is paused while team memberships are off. Anyone can chat right now.',
  chatOnTitle: 'Members-only squad chat',
  chatOnBody: 'Only members and your squad can type. Super Crits, Rumbles and lines from linked chats still show.',
  chatOnClosed: "New viewers can't join right now, so only current members and the squad will be able to type.",
  removeBody: "For members who break your squad chat rules. Their membership stops renewing and ends when their paid month does. They lose members-only squad chat and their team badge now, and they can't join your team again unless you allow it.",
  allowBody: 'They can join your team again after any current membership ends.',
  endAllLabel: 'End all team memberships',
  endAllTitle: 'End all team memberships?',
  endAllBody: "Every team membership ends at the end of the month it's paid for, and no one is charged again. Members keep their perks until then.",
  unavailableNotice: "New members can't join right now. Current members keep their perks and renewals.",
});

// Team-only lines (captain copy; 13.4).
const TEAM_ROSTER_HINT = 'Team memberships need at least 2 people on the roster who are verified and in good standing.';
const TEAM_VERIFY_ASK = 'Ask the people on your roster to verify their ID and age in Settings; anyone whose account is under review can contact support.';
const TEAM_WARNING_DISBAND = 'This team is pending disband. Its team memberships stop renewing and end at the end of the month each member paid for. If the team is saved, members can choose to keep theirs.';
const TEAM_WARNING_NO_PLAN = "Squad streaming starts with a paid Plan. Until you have one, your team page doesn't show Join.";
const TEAM_LOAD_FAILED = "Team memberships couldn't load.";
const TEAM_NONE = "You're not on a team yet.";
const TEAM_GONE = 'This team has disbanded.';
const TEAM_NOT_CAPTAIN = 'Your captain manages team memberships.';
const TEAM_NOT_ON_TEAM = "You're not on this team.";

// Team captain copy: the payee is the team's VERIFIED MEMBERS, shared
// equally among those who streamed with the team that month (the 30-minute
// rule is a qualifier, never a rate); the estimate is the team total.
export function teamIntroText(sharePct) {
  const pct = Math.round(Number(sharePct));
  return "Let viewers support your whole team every month. CritCash credits the team's verified members a share of "
    + `each membership payment, currently up to ${pct}% after processing costs, shared equally among those who `
    + 'streamed with the team that month. Shares become payable after the month ends, and not before CritCash has '
    + 'received the funds.';
}

export function teamRungEstimateText(estTeamNetCents) {
  return `About ${formatUsd(estTeamNetCents)} of each membership payment at this price is credited by CritCash `
    + "(an estimate, after processing costs), shared equally among the team's verified members who streamed with "
    + 'the team that month.';
}

// The captain's settings, or which read-only state (13.4): 'gone' (the team
// doc is missing), 'member' (a roster member who is not the captain),
// 'outsider' (not on this team: an old notification tap), else 'captain'.
export function teamViewState(data) {
  const el = data?.eligibility || {};
  // A captain payload is never 'gone' (a live team whose name sanitizes to
  // empty answers not_configured/team too): it keeps the management view.
  if (el.reason === 'not_configured' && el.cause === 'team' && data?.isCaptain !== true) return 'gone';
  if (el.reason === 'not_captain' || data?.isCaptain === false) {
    return data?.viewerOnRoster === true ? 'member' : 'outsider';
  }
  return 'captain';
}

export function membersHeading(members) {
  const n = Array.isArray(members?.list) ? members.list.length : 0;
  if (members?.truncated === true) return '500+ members';
  return `${n} ${n === 1 ? 'member' : 'members'}`;
}

export default function CreatorTeamMembershipsScreen({ navigation, route }) {
  const T = TEAM_TEXT;
  const routeTeamId = typeof route?.params?.teamId === 'string' && route.params.teamId
    ? route.params.teamId
    : null;
  const { showError } = useError();
  const { teamLive } = useMembershipsLive();
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [perkNotice, setPerkNotice] = useState(null);
  // One confirm at a time: {kind:'perkOff', perk} | {kind:'chatOn'} |
  // {kind:'remove', row} | {kind:'allow', row} | {kind:'endAll'}.
  const [confirm, setConfirm] = useState(null);
  // The team managed here, and "the caller is on no team" (only when
  // getMyStreamInfo answered with no teamMembershipEntry).
  const [teamId, setTeamId] = useState(routeTeamId);
  const [noTeam, setNoTeam] = useState(false);
  // Only the latest load writes state: a slower reply for the previous team
  // never overwrites the current one (the TeamPageScreen seqRef pattern).
  const seqRef = useRef(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    setLoading(true);
    setLoadError(null);
    try {
      let id = routeTeamId;
      if (!id) {
        // A bare /creator/team-memberships link: the caller's own team. A
        // failed call (or an older server that omits the key) is never read
        // as "no team".
        let entry;
        try {
          const res = await httpsCallable(functions, 'getMyStreamInfo')();
          entry = res?.data?.teamMembershipEntry;
        } catch (err) {
          console.error('[CreatorTeamMembershipsScreen] team lookup failed:', err);
          entry = undefined;
        }
        if (seqRef.current !== seq) return;
        if (entry === undefined) {
          setData(null);
          setNoTeam(false);
          setLoadError(TEAM_LOAD_FAILED);
          return;
        }
        if (!entry || typeof entry.teamId !== 'string' || !entry.teamId) {
          setData(null);
          setNoTeam(true);
          return;
        }
        id = entry.teamId;
      }
      setNoTeam(false);
      setTeamId(id);
      // Another team's settings never stay on screen while this one loads
      // (or after its load fails): the spinner or the error shows instead.
      setData((prev) => (prev && prev.teamId !== id ? null : prev));
      const next = await getMyTeamMembershipSettings({ teamId: id });
      if (seqRef.current !== seq) return;
      setData(next);
    } catch (err) {
      console.error('[CreatorTeamMembershipsScreen] load failed:', err);
      if (seqRef.current !== seq) return;
      setLoadError(membershipErrorMessage(err));
    } finally {
      if (seqRef.current === seq) setLoading(false);
    }
  }, [routeTeamId]);

  useEffect(() => {
    if (authReady && user) load();
    if (authReady && !user) setData(null);
  }, [authReady, user, load]);

  // The team every team write names (the payload's own id once loaded).
  const activeTeamId = (typeof data?.teamId === 'string' && data.teamId) || teamId;

  // After ANY failed change: the server's sentence, the confirm closed, and
  // the settings fetched again (eligibility.reason redraws the checklist).
  const failed = (err) => {
    logMembershipError('CreatorTeamMembershipsScreen change', err);
    setConfirm(null);
    showError(T.errorTitle, membershipErrorMessage(err));
    load();
  };

  const run = async (key, fn) => {
    setBusy(key);
    try {
      return await fn();
    } catch (err) {
      failed(err);
      return undefined;
    } finally {
      setBusy(null);
    }
  };

  // updateMyTeamMembershipSettings answers with the full settings shape.
  const applySettings = (next) => {
    if (next && typeof next === 'object') setData(next);
  };

  const updateSettings = (fields) => updateMyTeamMembershipSettings({ teamId: activeTeamId, ...fields });

  const setEnabled = (enabled) => run('enabled', async () => {
    setPerkNotice(null);
    applySettings(await updateSettings({ enabled }));
  });

  const setRung = (rungCents) => run('rung', async () => {
    setPerkNotice(null);
    applySettings(await updateSettings({ rungCents }));
  });

  const writePerk = (perk, on) => run(`perk:${perk}`, async () => {
    const current = data?.settings?.perks || {};
    const perks = {};
    TEAM_PERK_KEYS.forEach((k) => { perks[k] = current[k] === true; });
    perks[perk] = on;
    const next = await updateSettings({ perks });
    setConfirm(null);
    applySettings(next);
    setPerkNotice(!on && TEAM_SNAPSHOT_PERKS.includes(perk) ? 'Saved. Members will be notified.' : null);
  });

  const onPerkChange = (perk, on) => {
    setPerkNotice(null);
    // Removing a member perk asks first; the join line affects future joins only.
    if (!on && TEAM_SNAPSHOT_PERKS.includes(perk)) setConfirm({ kind: 'perkOff', perk });
    else writePerk(perk, on);
  };

  const setChat = (on) => run('chat', async () => {
    await setTeamChatMembersOnly({ teamId: activeTeamId, on });
    setConfirm(null);
    await load();
  });

  const setBlocked = (viewerUid, blocked) => run(`block:${viewerUid}`, async () => {
    await setTeamMemberBlocked({ teamId: activeTeamId, viewerUid, blocked });
    setConfirm(null);
    await load();
  });

  const endAll = () => run('endAll', async () => {
    await endAllMyTeamMemberships({ teamId: activeTeamId, confirm: true });
    setConfirm(null);
    await load();
  });

  const showMoreBlocked = () => run('blockedMore', async () => {
    const list = data?.blocked || [];
    const last = list[list.length - 1];
    if (!last) return;
    const page = await getMyTeamMembershipSettings({ teamId: activeTeamId, blockedAfter: last.viewerUid });
    setData((prev) => (prev ? {
      ...prev,
      blocked: [...(prev.blocked || []), ...(Array.isArray(page?.blocked) ? page.blocked : [])],
      blockedTruncated: page?.blockedTruncated === true,
    } : prev));
  });

  // --------------------------------------------------------------------------
  // Sections
  // --------------------------------------------------------------------------

  const linkTo = (label, target, params) => (
    <TouchableOpacity
      key={label}
      style={styles.teamLinkBtn}
      onPress={() => (params ? navigation.navigate(target, params) : navigation.navigate(target))}
      accessibilityRole="button"
    >
      <Text style={styles.linkBtnText}>{label}</Text>
    </TouchableOpacity>
  );

  // The checklist's remedy for each eligibility reason (13.4 item 1).
  const teamCheckAction = (reason) => {
    if (reason === 'unverified' || reason === 'captain_unverified') return linkTo('Verify your ID and age', 'Verification');
    if (reason === 'roster_too_small') return linkTo('Invite members', 'YourTeam');
    // The remedy is verification, not invitations; no link.
    if (reason === 'not_enough_verified') return <Text style={styles.hint}>{TEAM_VERIFY_ASK}</Text>;
    if (reason === 'never_streamed') return linkTo('Start a squad stream', 'YourTeam');
    if (reason === 'needs_team_plan') return linkTo('See Plans', 'Subscription');
    return null;
  };

  const renderChecklist = () => {
    const el = data.eligibility || {};
    const rows = [];
    // While the team is pending disband, its warning carries the state and
    // "Invite members" would be the wrong action.
    const suppressed = el.reason === 'roster_too_small' && data.disbandRequested === true;
    if (el.ok === false && el.message && !suppressed) {
      rows.push(
        <View key="elig" style={styles.checkRow}>
          <Ionicons name="alert-circle-outline" size={18} color={C.brand} style={styles.checkIcon} />
          <View style={styles.checkBody}>
            <Text style={styles.checkText}>{el.message}</Text>
            {teamCheckAction(el.reason)}
          </View>
        </View>
      );
    }
    if (data.termsCurrent === false) {
      rows.push(
        <View key="terms" style={styles.checkRow}>
          <Ionicons name="document-text-outline" size={18} color={C.brand} style={styles.checkIcon} />
          <View style={styles.checkBody}>
            <TouchableOpacity style={styles.linkBtn} onPress={() => navigation.navigate('TosAgreement')} accessibilityRole="button">
              <Text style={styles.linkBtnText}>Review and accept the updated Terms</Text>
            </TouchableOpacity>
          </View>
        </View>
      );
    }
    return rows.length ? <View style={styles.section}>{rows}</View> : null;
  };

  // How many on the roster can share (receivable). Never a per-member
  // figure, never minutes.
  const renderRoster = () => {
    const r = data.roster || {};
    const count = Number(r.count);
    if (!Number.isFinite(count)) return null;
    const receivable = Number(r.receivableCount) || 0;
    return (
      <View style={styles.section}>
        <Text style={styles.checkText}>{`${count} on the roster, ${receivable} verified and in good standing`}</Text>
        <Text style={styles.hint}>{TEAM_ROSTER_HINT}</Text>
      </View>
    );
  };

  // One line per warnings[] token (presentation only).
  const renderWarnings = () => {
    const tokens = Array.isArray(data.warnings) ? data.warnings : [];
    const lines = [];
    tokens.forEach((w) => {
      if (w === 'disband' && !lines.some((l) => l.key === 'disband')) {
        lines.push(
          <View key="disband">
            <Text style={styles.notice}>{TEAM_WARNING_DISBAND}</Text>
            {linkTo('Your team', 'YourTeam')}
          </View>
        );
      } else if (w === 'no_plan' && !lines.some((l) => l.key === 'no_plan')) {
        lines.push(
          <View key="no_plan">
            <Text style={styles.notice}>{TEAM_WARNING_NO_PLAN}</Text>
            {linkTo('See Plans', 'Subscription')}
          </View>
        );
      }
    });
    return lines.length ? <View style={styles.warnings}>{lines}</View> : null;
  };

  const renderAccept = () => {
    const s = data.settings || {};
    const on = s.enabled === true;
    return (
      <View style={styles.section}>
        <View style={styles.switchRow}>
          <Text style={styles.switchLabel}>Accept new members</Text>
          <Switch
            value={on}
            onValueChange={(v) => setEnabled(v)}
            disabled={!!busy}
            trackColor={{ false: '#333', true: C.brand }}
            thumbColor={on ? '#000' : '#888'}
            accessibilityLabel="Accept new members"
          />
        </View>
        {!on && (s.paused === true || s.ended === true) ? (
          <Text style={styles.hint}>{T.pausedHint}</Text>
        ) : null}
      </View>
    );
  };

  // The team's one price: a rung (3.5b 4.3; no tiers, founder call 1).
  const renderRungs = () => {
    const stored = Number(data.settings?.rungCents);
    const rungs = Array.isArray(data.rungs) ? data.rungs : [];
    if (!rungs.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Price</Text>
        {rungs.map((r) => {
          const selected = Number(r.rungCents) === stored;
          return (
            <TouchableOpacity
              key={r.rungCents}
              style={[styles.rungCard, selected && styles.rungCardOn]}
              onPress={() => { if (!selected) setRung(r.rungCents); }}
              disabled={!!busy}
              accessibilityRole="radio"
              accessibilityState={{ selected, disabled: !!busy }}
            >
              <View style={styles.rungTop}>
                <Text style={styles.rungPrice}>{formatMonthlyPrice(r.rungCents)}</Text>
                {selected ? <Ionicons name="checkmark-circle" size={20} color={C.brand} /> : null}
              </View>
              <Text style={styles.rungEst}>{teamRungEstimateText(r.estTeamNetCents)}</Text>
            </TouchableOpacity>
          );
        })}
        <Text style={styles.hint}>Changing your price only affects new members. Current members keep the price they joined at.</Text>
      </View>
    );
  };

  // The perks: plain on/off switches (a team has no tiers).
  const renderPerks = (available) => {
    const perks = data.settings?.perks || {};
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Member perks</Text>
        {TEAM_PERK_KEYS.map((k) => {
          const on = perks[k] === true;
          // Management-only: a perk can be removed, never added.
          const disabled = !!busy || (!available && !on);
          return (
            <View key={k} style={styles.switchRow}>
              <Text style={styles.switchLabel}>{perkLabel(k)}</Text>
              <Switch
                value={on}
                onValueChange={(v) => onPerkChange(k, v)}
                disabled={disabled}
                trackColor={{ false: '#333', true: C.brand }}
                thumbColor={on ? '#000' : '#888'}
                accessibilityLabel={perkLabel(k)}
              />
            </View>
          );
        })}
        <Text style={styles.hint}>{T.perkRules}</Text>
        {perkNotice ? <Text style={styles.notice}>{perkNotice}</Text> : null}
      </View>
    );
  };

  const renderChat = (available) => {
    const chat = data.chat || {};
    const membersOnly = chat.membersOnly === true;
    if (!membersOnly && !(available && chat.available === true)) return null;
    if (membersOnly && chat.paused === true) {
      return (
        <View style={styles.section}>
          <View style={styles.switchRow}>
            <Text style={styles.switchLabel}>{T.chatPausedLabel}</Text>
            <TouchableOpacity style={styles.smallBtn} onPress={() => setChat(false)} disabled={!!busy} accessibilityRole="button">
              <Text style={styles.smallBtnText}>Turn off</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.hint}>{T.chatPausedHint}</Text>
        </View>
      );
    }
    return (
      <View style={styles.section}>
        <View style={styles.switchRow}>
          <Text style={styles.switchLabel}>{T.chatLabel}</Text>
          <Switch
            value={membersOnly}
            onValueChange={(v) => (v ? setConfirm({ kind: 'chatOn' }) : setChat(false))}
            disabled={!!busy || (!membersOnly && !(available && chat.available === true))}
            trackColor={{ false: '#333', true: C.brand }}
            thumbColor={membersOnly ? '#000' : '#888'}
            accessibilityLabel={T.chatLabel}
          />
        </View>
      </View>
    );
  };

  const renderMembers = () => {
    const members = data.members || {};
    const raw = Array.isArray(members.list) ? members.list : [];
    // Team rows are pinned {viewerUid, userName, removed}: the row and its
    // confirms read `name`, so map it first (a missing name reads 'A member').
    const list = raw.map((m) => ({ ...m, name: typeof m?.userName === 'string' && m.userName ? m.userName : null }));
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{membersHeading(members)}</Text>
        {list.map((m) => (
          <View key={m.viewerUid} style={styles.memberRow}>
            <View style={styles.memberBody}>
              <Text style={styles.memberName} numberOfLines={1}>{m.name || 'A member'}</Text>
              <View style={styles.chipRow}>
                {m.ending === true ? <Text style={styles.chip}>Ending</Text> : null}
                {m.removed === true ? <Text style={[styles.chip, styles.chipWarn]}>Removed</Text> : null}
              </View>
            </View>
            {m.removed !== true ? (
              <TouchableOpacity
                style={styles.smallBtn}
                onPress={() => setConfirm({ kind: 'remove', row: m })}
                disabled={!!busy}
                accessibilityRole="button"
              >
                <Text style={styles.smallBtnDanger}>Remove member</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ))}
      </View>
    );
  };

  const renderBlocked = () => {
    const blocked = Array.isArray(data.blocked) ? data.blocked : [];
    if (!blocked.length) return null;
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Removed viewers</Text>
        {blocked.map((b) => (
          <View key={b.viewerUid} style={styles.memberRow}>
            <View style={styles.memberBody}>
              <Text style={styles.memberName} numberOfLines={1}>{b.name || 'A viewer'}</Text>
            </View>
            <TouchableOpacity
              style={styles.smallBtn}
              onPress={() => setConfirm({ kind: 'allow', row: b })}
              disabled={!!busy}
              accessibilityRole="button"
            >
              <Text style={styles.smallBtnText}>Allow again</Text>
            </TouchableOpacity>
          </View>
        ))}
        {data.blockedTruncated === true ? (
          <TouchableOpacity style={styles.linkBtn} onPress={showMoreBlocked} disabled={!!busy} accessibilityRole="button">
            <Text style={styles.linkBtnText}>Show more</Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  };

  const renderEndAll = () => {
    const list = data.members?.list || [];
    if (!list.length && data.settings?.enabled !== true) return null;
    return (
      <View style={styles.section}>
        <TouchableOpacity style={styles.dangerBtn} onPress={() => setConfirm({ kind: 'endAll' })} disabled={!!busy} accessibilityRole="button">
          <Text style={styles.dangerBtnText}>{T.endAllLabel}</Text>
        </TouchableOpacity>
      </View>
    );
  };

  // --------------------------------------------------------------------------
  // Confirm dialog
  // --------------------------------------------------------------------------

  const confirmSpec = () => {
    if (!confirm) return null;
    if (confirm.kind === 'perkOff') {
      return {
        title: `Turn off ${T.perkOffNames[confirm.perk]}?`,
        body: ["Each member keeps it until their first renewal at least 7 days from now, and we'll let your members know."],
        cancel: 'Keep it on',
        ok: 'Turn off',
        danger: true,
        onOk: () => writePerk(confirm.perk, false),
      };
    }
    if (confirm.kind === 'chatOn') {
      const body = [T.chatOnBody];
      // The TEAM flag decides whether new viewers can join (a dark team pilot
      // keeps the chat gate open while only admins can join).
      if (data?.settings?.paused === true || teamLive !== true) {
        body.push(T.chatOnClosed);
      }
      return { title: T.chatOnTitle, body, cancel: 'Cancel', ok: 'Turn on', onOk: () => setChat(true) };
    }
    if (confirm.kind === 'remove') {
      return {
        title: confirm.row?.name ? `Remove ${confirm.row.name}?` : 'Remove this member?',
        body: [T.removeBody],
        cancel: 'Keep member',
        ok: 'Remove',
        danger: true,
        onOk: () => setBlocked(confirm.row.viewerUid, true),
      };
    }
    if (confirm.kind === 'allow') {
      return {
        title: confirm.row?.name ? `Allow ${confirm.row.name} again?` : 'Allow this viewer again?',
        body: [T.allowBody],
        cancel: 'Cancel',
        ok: 'Allow',
        onOk: () => setBlocked(confirm.row.viewerUid, false),
      };
    }
    if (confirm.kind === 'endAll') {
      return {
        title: T.endAllTitle,
        body: [T.endAllBody],
        cancel: 'Keep them',
        ok: 'End all',
        danger: true,
        onOk: endAll,
      };
    }
    return null;
  };
  const spec = confirmSpec();

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  const available = data?.available === true;
  const pct = Number(data?.sharePct);
  // The captain's settings, or one read-only state (13.4 a-d).
  const teamState = data ? teamViewState(data) : null;

  // A read-only state: its line and its links, nothing else (no checklist,
  // so a payload's termsCurrent:false never shows the nudge).
  const renderTeamState = (line, links) => (
    <View style={styles.stateBlock}>
      <Text style={styles.stateText}>{line}</Text>
      {links}
    </View>
  );

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.inner}>
          <Text style={styles.title}>{T.title}</Text>

          {!authReady ? (
            <View style={styles.stateBlock}><ActivityIndicator color={C.brand} size="large" /></View>
          ) : !user ? (
            <View style={styles.stateBlock}>
              <Ionicons name="lock-closed" size={36} color="#666" />
              <Text style={styles.stateText}>{T.signIn}</Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={() => navigation.navigate('Login')} accessibilityRole="button">
                <Text style={styles.primaryBtnText}>Sign in</Text>
              </TouchableOpacity>
            </View>
          ) : noTeam && !loading ? (
            renderTeamState(TEAM_NONE, [linkTo('Your team', 'YourTeam')])
          ) : !data && (loading || !loadError) ? (
            <View style={styles.stateBlock}><ActivityIndicator color={C.brand} size="large" /></View>
          ) : !data ? (
            <View style={styles.stateBlock}>
              <Text style={styles.stateText}>{loadError}</Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={load} accessibilityRole="button">
                <Text style={styles.primaryBtnText}>Try again</Text>
              </TouchableOpacity>
            </View>
          ) : teamState === 'gone' ? (
            renderTeamState(TEAM_GONE, [linkTo('Your memberships', 'Memberships')])
          ) : teamState === 'member' ? (
            renderTeamState(TEAM_NOT_CAPTAIN, [
              linkTo('Team page', 'TeamPage', { teamId: activeTeamId }),
              linkTo('Your team', 'YourTeam'),
            ])
          ) : teamState === 'outsider' ? (
            renderTeamState(TEAM_NOT_ON_TEAM, [linkTo('Your team', 'YourTeam')])
          ) : (
            <>
              {Number.isFinite(pct) && pct > 0 ? <Text style={styles.intro}>{teamIntroText(pct)}</Text> : null}
              {available ? (
                <>
                  {renderChecklist()}
                  {renderRoster()}
                  {renderWarnings()}
                  {renderAccept()}
                  {renderRungs()}
                </>
              ) : (
                <>
                  {/* The warnings sit directly above the notice here: the
                      disband warning matters most when nothing can be sold. */}
                  {renderWarnings()}
                  <Text style={styles.notice}>{T.unavailableNotice}</Text>
                </>
              )}
              {renderPerks(available)}
              {renderChat(available)}
              {renderMembers()}
              {renderBlocked()}
              {renderEndAll()}
            </>
          )}
        </View>
      </ScrollView>

      <Modal visible={!!spec} transparent animationType="fade" onRequestClose={() => setConfirm(null)}>
        <View style={styles.dgOverlay}>
          <Pressable style={styles.dgScrim} onPress={() => setConfirm(null)} />
          {spec ? (
            <View style={styles.dgCard}>
              <Text style={styles.dgTitle}>{spec.title}</Text>
              {spec.body.map((line) => <Text key={line} style={styles.dgBody}>{line}</Text>)}
              <View style={styles.dgBtnRow}>
                <TouchableOpacity style={styles.dgCancel} onPress={() => setConfirm(null)} accessibilityRole="button">
                  <Text style={styles.dgCancelTxt}>{spec.cancel}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={spec.danger ? styles.dgDanger : styles.dgConfirm}
                  onPress={spec.onOk}
                  disabled={!!busy}
                  accessibilityRole="button"
                >
                  {busy
                    ? <ActivityIndicator size="small" color={spec.danger ? '#fff' : '#000'} />
                    : <Text style={spec.danger ? styles.dgDangerTxt : styles.dgConfirmTxt}>{spec.ok}</Text>}
                </TouchableOpacity>
              </View>
            </View>
          ) : null}
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.bg },
  scroll: { padding: 16, paddingBottom: 48 },
  inner: { width: '100%', maxWidth: 760, alignSelf: 'center' },
  title: { color: C.text, fontSize: 26, fontWeight: '800', marginBottom: 12 },
  intro: { color: C.textMuted, fontSize: 14, lineHeight: 21, marginBottom: 16 },
  notice: { color: C.text, fontSize: 13.5, lineHeight: 20, marginTop: 8, marginBottom: 12 },
  warnings: { marginBottom: 4 },

  stateBlock: { alignItems: 'center', paddingVertical: 48, paddingHorizontal: 16, gap: 12 },
  stateText: { color: C.textMuted, fontSize: 14, textAlign: 'center', maxWidth: 420, lineHeight: 20 },

  section: {
    backgroundColor: C.surface, borderWidth: 1, borderColor: C.border,
    borderRadius: THEME.radius.lg, padding: 16, marginBottom: 12,
  },
  sectionTitle: { color: C.text, fontSize: 16, fontWeight: '800', marginBottom: 10 },
  hint: { color: C.textMuted, fontSize: 12.5, lineHeight: 18, marginTop: 8 },

  checkRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 8 },
  checkIcon: { marginRight: 10, marginTop: 1 },
  checkBody: { flex: 1, minWidth: 0 },
  checkText: { color: C.text, fontSize: 13.5, lineHeight: 20 },
  linkBtn: { alignSelf: 'flex-start', paddingVertical: 6 },
  // Team links: at least 36 px tall.
  teamLinkBtn: { alignSelf: 'flex-start', paddingVertical: 6, minHeight: 36, justifyContent: 'center' },
  linkBtnText: { color: C.brand, fontSize: 13.5, fontWeight: '700' },

  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingVertical: 6 },
  switchLabel: { flex: 1, minWidth: 0, color: C.text, fontSize: 14 },

  rungCard: {
    backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, padding: 12, marginBottom: 8,
  },
  rungCardOn: { borderColor: C.brand },
  rungTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  rungPrice: { color: C.text, fontSize: 15, fontWeight: '800' },
  rungEst: { color: C.textMuted, fontSize: 12.5, lineHeight: 18, marginTop: 4 },

  memberRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 10, borderTopWidth: 1, borderTopColor: C.border,
  },
  memberBody: { flex: 1, minWidth: 0 },
  memberName: { color: C.text, fontSize: 14, fontWeight: '700' },
  chipRow: { flexDirection: 'row', gap: 6, marginTop: 4 },
  chip: {
    color: C.text, fontSize: 11, fontWeight: '800', backgroundColor: C.chip,
    borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2, overflow: 'hidden',
  },
  chipWarn: { color: '#ff9c9c' },
  smallBtn: {
    backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, paddingHorizontal: 12, paddingVertical: 8, flexShrink: 0,
  },
  smallBtnText: { color: C.text, fontSize: 12.5, fontWeight: '700' },
  smallBtnDanger: { color: '#ff6b6b', fontSize: 12.5, fontWeight: '700' },
  dangerBtn: {
    borderWidth: 1, borderColor: '#e5484d', borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center',
  },
  dangerBtnText: { color: '#ff6b6b', fontWeight: '800', fontSize: 14 },
  primaryBtn: {
    backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingHorizontal: 16, paddingVertical: 10, alignItems: 'center',
  },
  primaryBtnText: { color: '#000', fontWeight: '800', fontSize: 13.5 },

  dgOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
  dgScrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)' },
  dgCard: {
    width: '100%', maxWidth: 400, backgroundColor: C.surface, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.lg, padding: 20,
  },
  dgTitle: { color: C.text, fontSize: 17, fontWeight: '800', marginBottom: 10 },
  dgBody: { color: C.textMuted, fontSize: 13.5, lineHeight: 20, marginBottom: 10 },
  dgBtnRow: { flexDirection: 'row', gap: 10, marginTop: 4 },
  dgCancel: {
    flex: 1, backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center',
  },
  dgCancelTxt: { color: C.text, fontWeight: '700', fontSize: 13.5 },
  dgConfirm: { flex: 1, backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center' },
  dgConfirmTxt: { color: '#000', fontWeight: '800', fontSize: 13.5 },
  dgDanger: { flex: 1, backgroundColor: '#e5484d', borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center' },
  dgDangerTxt: { color: '#fff', fontWeight: '800', fontSize: 13.5 },
});
